'use client'

/**
 * UpgradeDialogProvider — the one shared "you need a higher plan" dialog
 * (docs/plans/TIER_GATING_PLAN.md §B.3/B.5). Mounted once app-wide in
 * `src/components/Providers.tsx` (same nesting level as ConfirmProvider).
 *
 * Two ways a dialog open request reaches this component:
 *   1. `useUpgradeDialog().openUpgradeDialog(featureKey)` — any component
 *      (lock badges, UpgradeGate, the seat-limit toast) dispatches the
 *      `jm:feature-not-in-plan` window event.
 *   2. The global 402 handler in `src/lib/api.ts` dispatches the SAME event
 *      directly (it runs outside React and can't use the hook) whenever any
 *      API call comes back `402 { code: "FEATURE_NOT_IN_PLAN" }` — so a
 *      surface we forgot to wrap with UpgradeGate still ends in this dialog
 *      instead of a raw error toast.
 *
 * No card fields, no checkout (Tony ruling 2026-09-19 — purchasing is
 * web-only and not built yet). ADMIN_FULL users get "Request upgrade", which
 * only notifies VentureSoft staff (POST /api/entitlements/upgrade-request) —
 * it never changes any entitlement itself.
 */
import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'

import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import { toast } from 'react-toastify'

import { FEATURE_META, minimumPlanFor, type Entitlements, type FeatureKey } from '@shared/entitlements'

import { api } from '@/lib/api'
import { usePermission } from '@/hooks/usePermission'
import { useFormatters, type Formatters } from '@/hooks/useCountryProfile'
import { useEntitlements } from '@/hooks/useEntitlements'
import { FEATURE_NOT_IN_PLAN_EVENT } from '@/hooks/useUpgradeDialog'
import { useEntitlementsCatalog } from '@/hooks/useEntitlementsCatalog'
import {
  getUpgradePlanRows,
  planRowLabel,
  isAddonActive,
  validateSeatQty,
  computeExtraSeatsTotal,
  moneyFractionDigitsFor,
  MIN_EXTRA_SEATS,
  MAX_EXTRA_SEATS,
} from '@/lib/upgradeOptions'

type OpenDetail = { feature: FeatureKey; minimumPlan?: string }

type DialogState = { kind: 'feature'; feature: FeatureKey } | { kind: 'seats' } | { kind: 'plans' } | null

export const UpgradeDialogContext = createContext<{
  openUpgradeDialog: (feature: FeatureKey) => void

  /** Seat-limit-reached path (TeamsView "Add seats" / "Move to a team plan") — no single feature key involved. */
  openSeatsUpgradeDialog: () => void

  /** Feature-less "browse plans" entry point (navbar PlanBadge's Upgrade button
   *  when Plan & Billing isn't enabled) — same table, no single target feature. */
  openPlansDialog: () => void
} | null>(null)

export function UpgradeDialogProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DialogState>(null)
  const [requestState, setRequestState] = useState<'idle' | 'sending' | 'sent'>('idle')
  const isAdminFull = usePermission('ADMIN_FULL')
  const { money } = useFormatters()
  const { data: entitlements } = useEntitlements()

  const catalogQuery = useEntitlementsCatalog({ enabled: state !== null })

  const openUpgradeDialog = useCallback((f: FeatureKey) => {
    setRequestState('idle')
    setState({ kind: 'feature', feature: f })
  }, [])

  const openSeatsUpgradeDialog = useCallback(() => {
    setRequestState('idle')
    setState({ kind: 'seats' })
  }, [])

  const openPlansDialog = useCallback(() => {
    setRequestState('idle')
    setState({ kind: 'plans' })
  }, [])

  // Global 402 handler (src/lib/api.ts) dispatches this same event directly.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<OpenDetail>).detail

      if (detail?.feature) openUpgradeDialog(detail.feature)
    }

    window.addEventListener(FEATURE_NOT_IN_PLAN_EVENT, handler)
    
return () => window.removeEventListener(FEATURE_NOT_IN_PLAN_EVENT, handler)
  }, [openUpgradeDialog])

  const close = () => setState(null)

  const feature = state?.kind === 'feature' ? state.feature : null
  const isSeatsMode = state?.kind === 'seats'
  const isPlansMode = state?.kind === 'plans'
  const meta = feature ? FEATURE_META[feature] : null
  const minPlan = feature ? minimumPlanFor(feature) : null

  // The catalog plans that actually include this feature key (or, for an
  // add-on-only feature, every plan — add-ons are on/off at every tier). In
  // seats mode, every TEAM plan is relevant (Solo has no extra seats). Plans
  // mode (feature-less "browse plans") has its own upgrade-only filtering in
  // <PlansModeBody> (web/src/lib/upgradeOptions.ts) — it doesn't use this list.
  const relevantPlans = useMemo(() => {
    const plans = catalogQuery.data?.plans ?? []

    if (isSeatsMode) return plans.filter(p => p.planKind === 'team')
    if (!feature) return []
    if (minPlan === 'Add-on') return plans

return plans.filter(p => p.featureKeys.includes(feature))
  }, [catalogQuery.data, feature, minPlan, isSeatsMode])

  const relevantAddon = useMemo(() => {
    if (!feature || minPlan !== 'Add-on') return null
    
return (catalogQuery.data?.addons ?? []).find(a => a.grantsFeatureKey === feature) ?? null
  }, [catalogQuery.data, feature, minPlan])

  const handleRequestUpgrade = async () => {
    setRequestState('sending')

    try {
      if (isSeatsMode) {
        if (entitlements?.planKind === 'solo' && relevantPlans[0]) {
          await api.post('/api/entitlements/upgrade-request', { kind: 'plan', targetTierId: relevantPlans[0].id, note: 'Requested from seat-limit prompt' })
        } else {
          await api.post('/api/entitlements/upgrade-request', { kind: 'extra_seats', extraSeats: 1, note: 'Requested from seat-limit prompt' })
        }
      } else if (feature) {
        if (minPlan === 'Add-on' && relevantAddon) {
          await api.post('/api/entitlements/upgrade-request', { kind: 'addon', addonKey: relevantAddon.key })
        } else if (relevantPlans[0]) {
          await api.post('/api/entitlements/upgrade-request', { kind: 'plan', targetTierId: relevantPlans[0].id })
        } else {
          await api.post('/api/entitlements/upgrade-request', { kind: 'plan', note: `Requesting ${meta?.label ?? feature}` })
        }
      }

      setRequestState('sent')
    } catch {
      setRequestState('idle')
      toast.error('Could not send the upgrade request — please try again.')
    }
  }

  return (
    <UpgradeDialogContext.Provider value={{ openUpgradeDialog, openSeatsUpgradeDialog, openPlansDialog }}>
      {children}
      {/* WEB-027: mounted globally like ConfirmDialog — same focus-trap first-click race, same fix. */}
      <Dialog open={state !== null} onClose={close} maxWidth='sm' fullWidth disableAutoFocus disableEnforceFocus>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <i className={isPlansMode ? 'tabler-credit-card' : isSeatsMode ? 'tabler-users' : 'tabler-lock'} style={{ fontSize: 20, opacity: 0.7 }} />
            {isPlansMode ? 'Upgrade your plan' : isSeatsMode ? 'Seat limit reached' : (meta?.label ?? 'Upgrade required')}
          </Box>
          <IconButton size='small' onClick={close}><i className='tabler-x' /></IconButton>
        </DialogTitle>
        <DialogContent>
          {isPlansMode ? (
            <PlansModeBody catalogQuery={catalogQuery} entitlements={entitlements} isAdminFull={isAdminFull} money={money} />
          ) : requestState === 'sent' ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <i className='tabler-circle-check' style={{ fontSize: 40, color: 'var(--mui-palette-success-main)' }} />
              <Typography variant='h6' sx={{ mt: 1 }}>We&rsquo;ll be in touch</Typography>
              <Typography variant='body2' color='text.secondary'>
                Your request has been sent. Our team will reach out to confirm it with you — nothing changes and nothing is charged until you approve.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                {isSeatsMode
                  ? (entitlements?.planKind === 'solo'
                      ? 'The Solo plan has 1 seat and no extra seats — adding a team member means moving to a team plan.'
                      : `You're using ${entitlements?.seats.used ?? '?'} of ${entitlements?.seats.limit ?? entitlements?.seats.included ?? '?'} seats on ${entitlements?.planName ?? 'your plan'}.`)
                  : minPlan === 'Add-on'
                    ? `${meta?.label ?? 'This module'} is an add-on module — available at any plan.`
                    : `${meta?.label ?? 'This feature'} is included starting on the ${minPlan} plan.`}
                {!isSeatsMode && entitlements?.planName ? ` Your current plan: ${entitlements.planName}.` : ''}
              </Typography>

              {catalogQuery.isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
              ) : minPlan === 'Add-on' && relevantAddon ? (
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                  <Typography variant='subtitle2'>{relevantAddon.name}</Typography>
                  {relevantAddon.description && (
                    <Typography variant='body2' color='text.secondary'>{relevantAddon.description}</Typography>
                  )}
                  <Typography variant='body2' sx={{ mt: 1, fontWeight: 600 }}>
                    {relevantAddon.priceMonthly != null ? `${money(relevantAddon.priceMonthly)}/mo` : 'Contact us for pricing'}
                  </Typography>
                </Box>
              ) : relevantPlans.length > 0 ? (
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Plan</TableCell>
                      <TableCell>Seats</TableCell>
                      <TableCell align='right'>Price</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {relevantPlans.map(p => {
                      const isCurrent = entitlements?.planId === p.id


return (
                        <TableRow key={p.id}>
                          <TableCell>
                            {p.name}
                            {isCurrent && <Chip label='Current' size='small' sx={{ ml: 1 }} />}
                          </TableCell>
                          <TableCell>{p.maxUsers ?? '—'}</TableCell>
                          <TableCell align='right'>
                            {p.priceMonthly != null ? `${money(p.priceMonthly)}/mo` : 'Contact us for pricing'}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant='body2' color='text.secondary'>Contact us for pricing.</Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={close} color='inherit'>Close</Button>
          {requestState !== 'sent' && !isPlansMode && (
            isAdminFull ? (
              <Button
                variant='contained'
                onClick={() => handleRequestUpgrade()}
                disabled={requestState === 'sending'}
                startIcon={requestState === 'sending' ? <CircularProgress size={14} color='inherit' /> : <i className='tabler-arrow-up-circle' />}
              >
                Request upgrade
              </Button>
            ) : (
              <Button variant='contained' disabled>Ask your administrator to upgrade</Button>
            )
          )}
        </DialogActions>
      </Dialog>
    </UpgradeDialogContext.Provider>
  )
}

type RowRequestState = { status: 'sending' | 'sent' | 'error'; message?: string }

/**
 * Plans-mode body (owner request 2026-09-21: "so for the upgrade panel only
 * show current and upgrade options. also need to include the options and
 * individual seat addition with qty field") — three sections: Plan, Add-ons,
 * Extra users. Filtering/arithmetic lives in `@/lib/upgradeOptions` (pure,
 * unit-tested); this component is presentation + its own per-row request
 * state so an admin can fire off more than one request without the dialog
 * closing or other rows locking up.
 */
function PlansModeBody({
  catalogQuery,
  entitlements,
  isAdminFull,
  money,
}: {
  catalogQuery: ReturnType<typeof useEntitlementsCatalog>
  entitlements: Entitlements | undefined
  isAdminFull: boolean
  money: Formatters['money']
}) {
  const [rowState, setRowState] = useState<Record<string, RowRequestState>>({})
  const [seatQtyInput, setSeatQtyInput] = useState('1')

  const sendRequest = async (key: string, payload: Record<string, unknown>) => {
    setRowState(s => ({ ...s, [key]: { status: 'sending' } }))
    try {
      await api.post('/api/entitlements/upgrade-request', payload)
      setRowState(s => ({ ...s, [key]: { status: 'sent' } }))
    } catch (err) {
      setRowState(s => ({ ...s, [key]: { status: 'error', message: err instanceof Error ? err.message : 'Could not send the request.' } }))
    }
  }

  if (catalogQuery.isLoading || !entitlements) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
  }

  if (catalogQuery.isError || !catalogQuery.data) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>Plans could not be loaded — try again.</Typography>
        <Button size='small' variant='outlined' startIcon={<i className='tabler-refresh' />} onClick={() => catalogQuery.refetch()}>
          Retry
        </Button>
      </Box>
    )
  }

  const catalog = catalogQuery.data
  const currentCatalogPlan = catalog.plans.find(p => p.id === entitlements.planId) ?? null
  const upgradeRows = getUpgradePlanRows(catalog.plans, { planKind: entitlements.planKind, tierRank: entitlements.tierRank })
  const allowsExtraSeats = currentCatalogPlan?.allowsExtraSeats === true && currentCatalogPlan.extraSeatPrice != null
  const { qty: seatQty, error: seatQtyError } = validateSeatQty(seatQtyInput)
  const seatTotal = computeExtraSeatsTotal(seatQty, currentCatalogPlan?.extraSeatPrice)

  const requestCell = (key: string, onRequest: () => void, disabled = false) => {
    const rs = rowState[key]

    if (rs?.status === 'sent') {
      return <Typography variant='caption' color='success.main'>Request sent — we will be in touch</Typography>
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
        <Button size='small' variant='outlined' disabled={disabled || rs?.status === 'sending'} onClick={onRequest}>Request</Button>
        {rs?.status === 'error' && <Typography variant='caption' color='error.main'>{rs.message}</Typography>}
      </Box>
    )
  }

  return (
    <Box>
      {!isAdminFull && (
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Ask your account administrator to request an upgrade.
        </Typography>
      )}

      {/* Section 1 — Plan */}
      <Typography variant='subtitle2' sx={{ mb: 1 }}>Plan</Typography>
      <Table size='small' sx={{ mb: upgradeRows.length === 0 ? 0.5 : 3 }}>
        <TableHead>
          <TableRow>
            <TableCell>Plan</TableCell>
            <TableCell>Seats</TableCell>
            <TableCell align='right'>Price per month</TableCell>
            {isAdminFull && <TableCell align='right' />}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>
              {entitlements.planName}
              <Chip label='Current' size='small' sx={{ ml: 1 }} />
            </TableCell>
            <TableCell>{currentCatalogPlan?.maxUsers ?? entitlements.seats.limit ?? '—'}</TableCell>
            <TableCell align='right'>
              {currentCatalogPlan?.priceMonthly != null
                ? money(currentCatalogPlan.priceMonthly, moneyFractionDigitsFor(currentCatalogPlan.priceMonthly))
                : '—'}
            </TableCell>
            {isAdminFull && <TableCell align='right' />}
          </TableRow>
          {upgradeRows.map(row => (
            <TableRow key={row.plan.id}>
              <TableCell>{planRowLabel(row)}</TableCell>
              <TableCell>{row.plan.maxUsers ?? '—'}</TableCell>
              <TableCell align='right'>
                {row.plan.priceMonthly != null ? money(row.plan.priceMonthly, moneyFractionDigitsFor(row.plan.priceMonthly)) : 'Contact us'}
              </TableCell>
              {isAdminFull && (
                <TableCell align='right'>
                  {requestCell(`plan:${row.plan.id}`, () => sendRequest(`plan:${row.plan.id}`, { kind: 'plan', targetTierId: row.plan.id }))}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {upgradeRows.length === 0 && (
        <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>You are on the top plan.</Typography>
      )}

      {/* Section 2 — Add-ons */}
      <Typography variant='subtitle2' sx={{ mb: 1 }}>Add-ons</Typography>
      {catalog.addons.length === 0 ? (
        <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>No add-ons available.</Typography>
      ) : (
        <Table size='small' sx={{ mb: 3 }}>
          <TableBody>
            {catalog.addons.map(addon => {
              const active = isAddonActive(entitlements, addon)


return (
                <TableRow key={addon.key}>
                  <TableCell>
                    <Typography variant='body2' sx={{ fontWeight: 500 }}>{addon.name}</Typography>
                    {addon.description && <Typography variant='caption' color='text.secondary'>{addon.description}</Typography>}
                  </TableCell>
                  <TableCell align='right'>
                    {addon.priceMonthly != null ? money(addon.priceMonthly, moneyFractionDigitsFor(addon.priceMonthly)) : 'Contact us'}
                  </TableCell>
                  <TableCell align='right'>
                    {active ? (
                      <Chip label='Active' size='small' color='success' variant='outlined' />
                    ) : isAdminFull ? (
                      requestCell(`addon:${addon.key}`, () => sendRequest(`addon:${addon.key}`, { kind: 'addon', addonKey: addon.key }))
                    ) : null}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}

      {/* Section 3 — Extra users */}
      <Typography variant='subtitle2' sx={{ mb: 1 }}>Extra users</Typography>
      {allowsExtraSeats && currentCatalogPlan ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant='body2' color='text.secondary'>
            Your plan includes {currentCatalogPlan.maxUsers ?? entitlements.seats.included} users. Add more at{' '}
            {money(currentCatalogPlan.extraSeatPrice as number, moneyFractionDigitsFor(currentCatalogPlan.extraSeatPrice))} per user / month.
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            Currently using {entitlements.seats.used} of {entitlements.seats.limit ?? '—'} seats
            {entitlements.seats.extra > 0 ? ` (${entitlements.seats.extra} extra already purchased)` : ''}.
          </Typography>
          {isAdminFull && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
              <TextField
                size='small'
                type='number'
                label='Additional seats'
                value={seatQtyInput}
                onChange={e => setSeatQtyInput(e.target.value)}
                error={!!seatQtyError}
                helperText={seatQtyError ?? ' '}
                slotProps={{ htmlInput: { min: MIN_EXTRA_SEATS, max: MAX_EXTRA_SEATS, step: 1 } }}
                sx={{ width: 170 }}
              />
              <Typography variant='body2' sx={{ fontWeight: 600, pt: 1.5 }}>
                {seatTotal != null ? `= ${money(seatTotal, moneyFractionDigitsFor(seatTotal))} / month` : '—'}
              </Typography>
              <Box sx={{ pt: 0.5 }}>
                {requestCell('seats', () => sendRequest('seats', { kind: 'extra_seats', extraSeats: seatQty }), seatQty == null)}
              </Box>
            </Box>
          )}
        </Box>
      ) : (
        <Typography variant='body2' color='text.secondary'>
          Single-user plans cannot add users — move to a team plan.
        </Typography>
      )}

      <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 3 }}>
        Requests go to the JobMaster360 team. Nothing is charged here.
      </Typography>
    </Box>
  )
}
